import { gql } from "@apollo/client";
import { LRUMap } from "lru_map";
import * as React from "react";
import { useRequiredSession } from "src/components/session/session";
import {
  executionCache,
  ExecutionFinal,
  pollForFinalExecution,
  isExecutionFinal,
  isExecutionPending,
} from "src/gql/execution/execution";
import {
  GetTablePreviewDocument,
  GetTablePreviewMutation,
  GetTablePreviewMutationVariables,
  GetTablePreviewResponse,
} from "src/gql/codegen/graphql";
import { Session } from "src/utils/users/types";
import { apolloCore } from "src/utils/apollo/apollo";

type FetchResult<TData, TError> =
  | { state: "loading" }
  | { state: "data"; data: TData }
  | { state: "error"; error: TError };

type TablePreview = {
  queryId: number;
  execution: ExecutionFinal;
};

export function useTablePreview(table: string) {
  const session = useRequiredSession();
  const [tablePreview, setTablePreview] = React.useState<
    FetchResult<TablePreview, unknown>
  >(getCachedFetchResult(table) ?? { state: "loading" });

  React.useEffect(() => {
    const controller = new AbortController();

    const handleTablePreview = (tablePreview: TablePreview) => {
      setTablePreview({ state: "data", data: tablePreview });
    };

    const handleError = (error: unknown) => {
      setTablePreview({ state: "error", error });
    };

    fetchTablePreview(
      table,
      session,
      controller.signal,
      handleTablePreview
    ).catch(handleError);

    return () => {
      controller.abort();
    };
  }, [table]);

  return tablePreview;
}

function getCachedFetchResult(
  table: string
): FetchResult<TablePreview, unknown> | null {
  const cachedTablePreview = tablePreviewCache.get(table);

  if (cachedTablePreview === undefined) {
    return null;
  }

  const executionId =
    cachedTablePreview.result_id ?? cachedTablePreview.error_id ?? null;

  if (executionId === null) {
    return null;
  }

  const cachedExecution = executionCache.get(executionId);

  if (cachedExecution === undefined || isExecutionPending(cachedExecution)) {
    return null;
  }

  return {
    state: "data",
    data: {
      queryId: cachedTablePreview.query_id,
      execution: cachedExecution,
    },
  };
}

async function fetchTablePreview(
  table: string,
  session: Session,
  signal: AbortSignal,
  onTablePreview: (tablePreview: TablePreview) => void
) {
  const {
    job_id = null,
    result_id = null,
    error_id = null,
    query_id,
    parameters,
  } = await callGetTablePreview(table, session);

  const pollExecutions = async (executionId: string) => {
    const executions = pollForFinalExecution(
      executionId,
      query_id,
      parameters,
      signal
    );

    for await (const execution of executions) {
      if (isExecutionFinal(execution)) {
        onTablePreview({ queryId: query_id, execution });
      }
    }
  };

  if (result_id !== null) {
    await pollExecutions(result_id);
  } else if (error_id !== null) {
    await pollExecutions(error_id);
  }

  if (job_id !== null) {
    await pollExecutions(job_id);
  }
}

const tablePreviewCache = new LRUMap<string, GetTablePreviewResponse>(100);

async function callGetTablePreview(table: string, session: Session) {
  const cachedTablePreview = tablePreviewCache.get(table);

  if (cachedTablePreview !== undefined) {
    return cachedTablePreview;
  }

  const { data } = await apolloCore.mutate<
    GetTablePreviewMutation,
    GetTablePreviewMutationVariables
  >({
    mutation: GetTablePreviewDocument,
    variables: { table },
    context: { session },
  });

  if (data === null || data === undefined) {
    throw new Error("Response from 'get_table_preview' was empty");
  }

  const tablePreview = data.get_table_preview;
  tablePreviewCache.set(table, tablePreview);
  return tablePreview;
}

gql`
  mutation GetTablePreview($table: String!) {
    get_table_preview(table: $table) {
      job_id
      result_id
      error_id
      query_id
      parameters {
        key
        type
        value
      }
    }
  }
`;
